import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Clock, MessageCircle, Repeat, FileText, Truck, RotateCcw, CreditCard } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import axios from 'axios';

const API = window.location.origin + '/api';
const SLOT_LABELS = { morning: 'Morning (8AM-12PM)', afternoon: 'Afternoon (12PM-4PM)', evening: 'Evening (4PM-8PM)' };
const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 Weeks' },
  { value: 'monthly', label: 'Monthly' }
];
const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export default function OrderHistory() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [waLink, setWaLink] = useState('');
  const [recurModal, setRecurModal] = useState(null);
  const [frequency, setFrequency] = useState('weekly');
  const [preferredDay, setPreferredDay] = useState('monday');
  const [saving, setSaving] = useState(false);
  const [reorderingId, setReorderingId] = useState('');
  const { fetchCart } = useCart();
  const navigate = useNavigate();

  const handleReorder = async (order) => {
    setReorderingId(order.id);
    try {
      const { data } = await axios.post(`${API}/orders/${order.id}/reorder`, {}, { withCredentials: true });
      await fetchCart();
      if (data.added > 0) {
        if (data.skipped_out_of_stock || data.skipped_missing) {
          alert(data.message);
        }
        navigate('/cart');
      } else {
        alert('Sorry — none of the items from this order are currently available.');
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not reorder');
    } finally {
      setReorderingId('');
    }
  };

  const handleMakeRecurring = async () => {
    if (!recurModal) return;
    setSaving(true);
    try {
      await axios.post(`${API}/recurring-orders/from-order/${recurModal.id}`, { frequency, preferred_day: preferredDay }, { withCredentials: true });
      setRecurModal(null);
      alert('Recurring order created! Check "My Subscriptions" to manage it.');
    } catch (err) {
      console.error('Failed to create subscription:', err);
      alert(err.response?.data?.detail || 'Failed to create subscription');
    }
    setSaving(false);
  };

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };

  useEffect(() => {
    axios.get(`${API}/orders`, { withCredentials: true })
      .then(r => { setOrders(r.data); setLoading(false); })
      .catch(err => { console.error('Failed to load orders:', err); setLoading(false); });
    axios.get(`${API}/whatsapp/order-link`)
      .then(r => { if (r.data.configured) setWaLink(r.data.link); })
      .catch(err => console.error('Failed to load WhatsApp link:', err));
  }, []);

  return (
    <div className="orders-page" data-testid="orders-page">
      <button className="back-btn" onClick={() => navigate('/')} data-testid="back-home-btn">
        <ArrowLeft /> Home
      </button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>My Orders</h2>
        <button onClick={() => navigate('/subscriptions')} data-testid="subscriptions-link"
          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', border: '1px solid var(--lime)', background: 'white', color: 'var(--navy)', fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>
          <Repeat size={15} /> My Subscriptions
        </button>
      </div>

      {loading ? <div className="loading-screen">Loading orders...</div> :
        orders.length === 0 ? (
          <div className="empty-state">
            <ShoppingBag size={64} />
            <h3>No orders yet</h3>
            <p>Start shopping to see your orders here</p>
          </div>
        ) : (
          orders.map(order => (
            <div className="order-card" key={order.id} data-testid={`order-card-${order.id}`}>
              <div className="order-header">
                <span className="order-id">#{order.id.slice(0, 8)}</span>
                <span className={`order-status status-${order.status}`}>{order.status.replace(/_/g, ' ')}</span>
              </div>
              {order.supermarket_name && (
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '4px' }}>
                  From: <strong>{order.supermarket_name}</strong>
                </div>
              )}
              <div className="order-items-list">
                {order.items.map((item, i) => (
                  <span key={`${order.id}-${item.product_id || item.name}-${i}`}>{item.name} x{item.quantity}{i < order.items.length - 1 ? ', ' : ''}</span>
                ))}
              </div>
              {(order.delivery_date || order.delivery_time_slot) && (
                <div style={{ fontSize: '0.82rem', marginBottom: '8px', padding: '6px 10px', background: '#EFF6FF', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '6px', color: '#1E40AF' }}>
                  <Clock size={13} /> {order.delivery_date || ''} &mdash; {SLOT_LABELS[order.delivery_time_slot] || order.delivery_time_slot || ''}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <span className="order-total">{order.total.toFixed(2)} PGK</span>
                <span className="order-date">{formatDate(order.created_at)}</span>
              </div>
              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                {order.status !== 'cancelled' && order.status !== 'delivered' && (
                  <button onClick={() => navigate(`/track/${order.id}`)}
                    data-testid={`track-order-${order.id}`}
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '8px', border: 'none', background: 'var(--navy)', color: 'white', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                    <Truck size={13} /> Track Order
                  </button>
                )}
                <button onClick={() => { setRecurModal(order); setFrequency('weekly'); setPreferredDay('monday'); }}
                  data-testid={`make-recurring-${order.id}`}
                  style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--lime)', background: 'white', color: 'var(--navy)', fontSize: '0.8rem', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>
                  <Repeat size={13} /> Make Recurring
                </button>
                <button onClick={() => handleReorder(order)} disabled={reorderingId === order.id}
                  data-testid={`reorder-${order.id}`}
                  style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '8px', border: 'none', background: 'var(--lime)', color: 'var(--navy)', fontSize: '0.8rem', fontWeight: 700, cursor: reorderingId === order.id ? 'wait' : 'pointer', fontFamily: 'inherit', opacity: reorderingId === order.id ? 0.6 : 1 }}>
                  <RotateCcw size={13} /> {reorderingId === order.id ? 'Adding…' : 'Reorder'}
                </button>
                {order.payment_status !== 'paid' && (
                  <button
                    onClick={async () => {
                      try {
                        const { data } = await axios.post(`${API}/payments/initiate/order`, { order_id: order.id }, { withCredentials: true });
                        if (data.checkout_url) window.location.href = data.checkout_url;
                      } catch (err) {
                        alert(err.response?.data?.detail || 'Could not start payment');
                      }
                    }}
                    data-testid={`pay-online-${order.id}`}
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '8px', border: 'none', background: '#0369A1', color: 'white', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                    <CreditCard size={13} /> Pay online
                  </button>
                )}
                {waLink && (
                  <a href={`${waLink}?text=${encodeURIComponent(`Hi StoreRuns! I'd like to check on my order #${order.id.slice(0,8)}.`)}`}
                    target="_blank" rel="noopener noreferrer" data-testid={`whatsapp-order-${order.id}`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '8px', background: '#25D366', color: 'white', fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none' }}>
                    <MessageCircle size={13} /> WhatsApp
                  </a>
                )}
                {order.status === 'delivered' && (
                  <a href={`${API}/orders/${order.id}/receipt`} target="_blank" rel="noopener noreferrer"
                    data-testid={`receipt-${order.id}`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--border-gray)', background: 'var(--white)', color: 'var(--navy)', fontSize: '0.8rem', fontWeight: 500, textDecoration: 'none' }}>
                    <FileText size={13} /> Receipt
                  </a>
                )}
              </div>
            </div>
          ))
        )
      }

      {/* Make Recurring Modal */}
      {recurModal && (
        <div className="modal-overlay" onClick={() => setRecurModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} data-testid="recurring-modal">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Repeat size={20} /> Make This a Recurring Order
            </h3>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '16px' }}>
              We&apos;ll automatically place this order on your chosen schedule:
              <div style={{ marginTop: '8px', padding: '8px 12px', background: 'var(--light-gray)', borderRadius: '8px' }}>
                {recurModal.items.map((item, i) => <span key={`${recurModal.id}-mod-${item.product_id || item.name}-${i}`}>{item.name} x{item.quantity}{i < recurModal.items.length - 1 ? ', ' : ''}</span>)}
                <div style={{ fontWeight: 600, marginTop: '4px', color: 'var(--navy)' }}>{recurModal.total.toFixed(2)} PGK</div>
              </div>
            </div>
            <div className="form-group">
              <label>How often?</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {FREQUENCIES.map(f => (
                  <button key={f.value} type="button" onClick={() => setFrequency(f.value)}
                    style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--border-gray)', background: frequency === f.value ? 'var(--navy)' : 'var(--white)', color: frequency === f.value ? 'white' : 'var(--text-dark)', fontFamily: 'inherit', fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer' }}>
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>Preferred delivery day?</label>
              <select value={preferredDay} onChange={e => setPreferredDay(e.target.value)} style={{ textTransform: 'capitalize' }}>
                {DAYS.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
              </select>
            </div>
            <button className="btn-lime" onClick={handleMakeRecurring} disabled={saving} data-testid="confirm-recurring-btn" style={{ marginTop: '8px' }}>
              {saving ? 'Setting up...' : 'Set Up Recurring Order'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
